/* @flow */

import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Button from '@material-ui/core/Button';

import ButtonGroup from 'components/ButtonGroup/ButtonGroup';
import type TypeForm from 'forms/_.extend';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import UserStore from 'stores/UserStore';

import styles from './ReviewFormControls.module.scss';
import { generateActionUrl } from 'utils/NotificationUtils';
import { actionButton, viewButton } from '../../../../permissions/notifications';
import { RequireForAccess } from 'react-access';
import LoadingWrapper from '../../../../components/LoadingWrapper/LoadingWrapper';

export type ReviewFormControlsProps = {
  notification: Object,
  form: TypeForm,
  inEditMode: boolean,
  handleAction: Function,
  callback: Function,
};

type InjectedProps = {
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('userStore')
@observer
export default class ReviewFormControls extends ComponentWithInjectedProps<
  ReviewFormControlsProps,
  {},
  InjectedProps
> {
  renderButtons = (action: Object, index: number) => {
    const { t } = this.props;
    const url = `/${generateActionUrl(action)}`;
    const isLink = action.type === 'view' && action.namespace === 'notification';

    return (
      <RequireForAccess permissions={viewButton}>
        <Link to={url} key={index}>
          <Button variant="contained">
            {isLink
              ? t('general:actions.review')
              : t(`general:actions.${action.type}_${action.namespace}`)}
          </Button>
        </Link>
      </RequireForAccess>
    );
  };

  render() {
    const {
      t,
      notification,
      inEditMode,
      form,
      handleAction,
      callback,
      userStore: { isReadOnly },
    } = this.props;

    return (
      <ButtonGroup>
        <Link to={{ pathname: '/notifications', state: { tab: 1 } }}>
          <Button color="secondary" variant="contained">
            {t('general:actions.cancel')}
          </Button>
        </Link>
        <div className={styles['review-controls']}>
          <React.Fragment>
            {notification.actions
              .filter((action: Object) => {
                return action.type === 'view';
              })
              .map(this.renderButtons)}
          </React.Fragment>
          <React.Fragment>
            {notification.actions
              .filter((action: Object) => {
                return action.type !== 'view';
              })
              .map((action: Object, index: number) => {
                const primaries: Array<string> = [
                  'approve',
                  'approved',
                  'accept',
                  'accepted',
                  'merge',
                  'update',
                ];

                if (
                  notification.event_type === 'ManufacturerAdditionRequest' ||
                  notification.event_type === 'ProductAdditionRequest'
                ) {
                  if (inEditMode) {
                    if (action.type === 'approve') {
                      return false;
                    }
                  } else {
                    if (action.type === 'merge' || action.type === 'update') {
                      return false;
                    }
                  }
                }

                return (
                  <RequireForAccess permissions={actionButton}>
                    <Button
                      key={index.toString()}
                      color={primaries.includes(action.type) ? 'primary' : 'secondary'}
                      variant="contained"
                      disabled={isReadOnly}
                      onClick={(evt: SyntheticInputEvent) => {
                        if (primaries.includes(action.type) && form) {
                          form.onSubmit(evt);
                        } else {
                          handleAction(
                            action,
                            notification.template_values,
                            action.fields,
                            callback
                          );
                        }
                      }}
                    >
                      {t(`general:actions.${action.type}`)}
                    </Button>
                  </RequireForAccess>
                );
              })}
          </React.Fragment>
        </div>
      </ButtonGroup>
    );
  }
}
